import type { SiteDto, UpdateSiteDto } from '@/api/generated/schemas';

import { formatABN, formatACN } from './formatting';

/**
 * Prepares an update payload for a single field update.
 * Handles both top-level fields and nested fields (e.g., "siteFeatures.meterCount")
 *
 * @param fieldPath - Dot-notation path to the field (e.g., "supplyAddress" or "siteFeatures.meterCount")
 * @param value - The new value for the field
 * @param site - The current site object (needed for merging nested objects)
 * @returns UpdateSiteDto with only the changed field
 *
 * @example
 * ```typescript
 * // Top-level field
 * prepareFieldUpdate('supplyAddress', '123 Main St', site);
 * // Returns: { supplyAddress: '123 Main St' }
 *
 * // Nested field
 * prepareFieldUpdate('siteFeatures.meterCount', 5, site);
 * // Returns: { siteFeatures: { ...site.siteFeatures, meterCount: 5 } }
 * ```
 */
export const prepareFieldUpdate = (
  fieldPath: string,
  value: string | number | boolean | null,
  site: SiteDto,
): UpdateSiteDto => {
  const parts = fieldPath.split('.');

  // Strip whitespace from ACN and ABN before saving
  let processedValue = value;
  if (fieldPath === 'businessInfo.acn' || fieldPath === 'businessInfo.abn') {
    if (processedValue && typeof processedValue === 'string') {
      processedValue = processedValue.replace(/\s/g, '');
    }
  }

  if (parts.length === 1) {
    // Top-level field
    return { [fieldPath]: processedValue } as UpdateSiteDto;
  }

  // Nested field - merge with existing object
  const [parent, child] = parts;
  const parentKey = parent as keyof SiteDto;
  const existingParent = site[parentKey];

  return {
    [parent]: {
      ...(existingParent as object),
      [child]: processedValue,
    },
  } as UpdateSiteDto;
};

/**
 * Validates a field value based on its path and type
 *
 * @param fieldPath - Dot-notation path to the field
 * @param value - The value to validate
 * @returns Error message string if invalid, null if valid
 */
export const validateField = (fieldPath: string, value: string | number | boolean | null): string | null => {
  // Required fields
  if (fieldPath === 'supplyAddress') {
    if (!value || !String(value).trim()) {
      return 'Supply address is required';
    }
  }

  // Positive integer validation for counts
  if (fieldPath === 'siteFeatures.meterCount' || fieldPath === 'siteFeatures.lotCount') {
    if (value !== null && value !== undefined && value !== '') {
      const numValue = Number(value);
      if (isNaN(numValue)) {
        return 'Value must be a number';
      }
      if (numValue < 0) {
        return 'Value must be positive';
      }
      if (!Number.isInteger(numValue)) {
        return 'Value must be a whole number';
      }
    }
  }

  // Time validation (HH:MM format)
  if (fieldPath === 'siteFeatures.openingHour' || fieldPath === 'siteFeatures.closingHour') {
    if (value !== null && value !== undefined && value !== '') {
      const timeRegex = /^([0-1]?[0-9]|2[0-3]):[0-5][0-9]$/;
      if (!timeRegex.test(String(value))) {
        return 'Time must be in HH:MM format (e.g., 09:00)';
      }
    }
  }

  // Positive number validation for sizes and factors
  if (
    fieldPath === 'siteFeatures.solarSize' ||
    fieldPath === 'siteFeatures.batterySize' ||
    fieldPath === 'siteFeatures.transformerSize'
  ) {
    if (value !== null && value !== undefined && value !== '') {
      const numValue = Number(value);
      if (isNaN(numValue)) {
        return 'Value must be a number';
      }
      if (numValue < 0) {
        return 'Value must be positive';
      }
    }
  }

  // Power factor validation (typically 0-1)
  if (fieldPath === 'siteFeatures.powerFactor') {
    if (value !== null && value !== undefined && value !== '') {
      const numValue = Number(value);
      if (isNaN(numValue)) {
        return 'Power factor must be a number';
      }
      if (numValue < 0 || numValue > 1) {
        return 'Power factor must be between 0 and 1';
      }
    }
  }

  // ABN validation (11 digits)
  if (fieldPath === 'businessInfo.abn') {
    if (value !== null && value !== undefined && value !== '') {
      const abnRegex = /^\d{11}$/;
      if (!abnRegex.test(String(value).replace(/\s/g, ''))) {
        return 'ABN must be 11 digits';
      }
    }
  }

  // ACN validation (9 digits)
  if (fieldPath === 'businessInfo.acn') {
    if (value !== null && value !== undefined && value !== '') {
      const acnRegex = /^\d{9}$/;
      if (!acnRegex.test(String(value).replace(/\s/g, ''))) {
        return 'ACN must be 9 digits';
      }
    }
  }

  // Optional text fields - just check they're strings if provided
  if (
    fieldPath === 'networkInfo.distributor' ||
    fieldPath === 'networkInfo.tariffCode' ||
    fieldPath === 'networkInfo.dlfCode' ||
    fieldPath === 'networkInfo.tlfCode' ||
    fieldPath === 'siteFeatures.transformerNumber' ||
    fieldPath === 'billingInfo.retailer' ||
    fieldPath === 'billingInfo.plan' ||
    fieldPath === 'billingInfo.billingAddress' ||
    fieldPath === 'businessInfo.businessType'
  ) {
    if (value !== null && value !== undefined && value !== '') {
      if (typeof value !== 'string') {
        return 'Value must be text';
      }
    }
  }

  return null; // Valid
};

/**
 * Gets the current value of a field from a site object using dot notation
 * Formats ACN and ABN values for display
 *
 * @param fieldPath - Dot-notation path to the field
 * @param site - The site object
 * @returns The field value, or null if not found
 */
export const getFieldValue = (fieldPath: string, site: SiteDto): string | number | boolean | null | undefined => {
  const parts = fieldPath.split('.');

  let value: unknown;
  if (parts.length === 1) {
    value = site[fieldPath as keyof SiteDto];
  } else {
    const [parent, child] = parts;
    const parentObj = site[parent as keyof SiteDto];

    if (parentObj && typeof parentObj === 'object') {
      value = (parentObj as Record<string, unknown>)[child];
    } else {
      value = null;
    }
  }

  // Format ACN and ABN for display
  if (fieldPath === 'businessInfo.acn') {
    const formatted = formatACN(value as string | undefined);
    return formatted === '-' ? '' : formatted;
  }
  if (fieldPath === 'businessInfo.abn') {
    const formatted = formatABN(value as string | undefined);
    return formatted === '-' ? '' : formatted;
  }

  return value as string | number | boolean | null | undefined;
};
